import type { ReactNode } from 'react'
import { useCitations } from '../../citation/useCitations.js'
import { sanitizeCitationHtml } from '../../citation/sanitizeHtml.js'
import type { SectionProps } from './kinds.js'
import { StyleCredit } from '../../citation/StyleCredit.js'

/**
 * De referentielijst: het vaste component dat uit de citaties in de rich text van het voorstel
 * groeit (R-50, eisenboek 12.9), naar CitatenPOC's `Bibliography.tsx` (bijlage C.2). In het
 * formulier komt de lijst van de schil, over de secties van de laag in de projectie van de lezer
 * (`useSectionCitations`); zonder schil rekent het component over zijn eigen sectie.
 *
 * **Puur afgeleid.** Deze control bewaart niets: `gebruikCitaties` berekent de lijst uit de
 * bronnenopslag en de citatieclusters in de rich-text-fragmenten van de sectie. Twee secties met
 * dezelfde inhoud tonen dezelfde lijst.
 *
 * **Gesaniteerd vóór weergave.** citeproc levert opgemaakte HTML per regel, opgebouwd uit
 * brondata die de invuller zelf intypt (R-50); `sanitizeCitatieHtml` is verdediging in de diepte
 * vóór die HTML via `dangerouslySetInnerHTML` in de DOM komt.
 */
export function ReferenceList({
  taal: language,
  koppeling: link,
  doc,
  richtextVeldIds: richtextFieldIds,
  citations,
  bewerkbaar: editable,
}: SectionProps): ReactNode {
  // Met een uitkomst van de schil rekent het component niet zelf (`null` legt de eigen berekening stil).
  const own = useCitations(citations === undefined ? doc : null, richtextFieldIds)
  const referenceList = (citations ?? own).literatuurlijst

  return (
    <div className="ef-referentielijst" id={link.id} aria-describedby={link['aria-describedby']}>
      {referenceList.length === 0 ? (
        <>
          <p className="ef-referentielijst__leeg">
            {language === 'nl' ? 'Nog geen bronvermeldingen.' : 'No citations yet.'}
          </p>
          {/* Hoe u een bron citeert, voor wie kan schrijven (zoals de lege lijst in CitatenPOC). */}
          {editable ? (
            <p className="ef-referentielijst__leeg">
              {language === 'nl'
                ? 'Typ @ in de tekst of kies "Bron citeren" om een bron te citeren.'
                : 'Type @ in the text or choose "Cite a source".'}
            </p>
          ) : null}
        </>
      ) : (
        <>
        <ul className="ef-referentielijst__lijst">
          {referenceList.map((rule) => (
            <li
              key={rule.bronId}
              className="ef-referentielijst__regel"
              // eslint-disable-next-line react/no-danger -- gesaniteerd, zie de kop hierboven
              dangerouslySetInnerHTML={{ __html: sanitizeCitationHtml(rule.html) }}
            />
          ))}
        </ul>
        <StyleCredit taal={language} />
        </>
      )}
    </div>
  )
}
